import { StorageProviderKey } from '../../models/storageProviderKeys';
import IStorageProvider from './istorageProvider';

/**
 * A storage provider that keeps the library in this browser and nowhere else.
 *
 * **Development only.** Every real provider is a cloud account behind OAuth,
 * which is right for a music library that has to follow you between machines
 * and wrong for a browser test: App.tsx renders no routes at all until a
 * provider is configured, so without this a headless browser gets as far as
 * the setup screen and stops. Signing a test harness into a real Google
 * account instead would put the user's actual library in reach of it, which
 * is a bad trade for a smoke test.
 *
 * It is registered only when NODE_ENV is not production (contextService.tsx),
 * so a deployed build cannot select it and nobody can end up with their
 * library trapped in one browser's localStorage by accident.
 *
 * Selected the same way as any other provider - `storageProvider` in
 * localStorage - which is what lets a test seed it before the app loads
 * rather than clicking through setup. See e2e/README.md.
 */

/** Whether this provider exists at all in the current build. */
export const localStorageProviderEnabled = process.env.NODE_ENV !== 'production';

/** Namespaced so it cannot collide with the app's own localStorage keys. */
const filePrefix = 'fadila.localProvider/';

/**
 * Plain JSON, uncompressed - unlike the cloud providers, which gzip because
 * they are paying for a round trip. The 5 MB localStorage quota is the real
 * limit here, and a library big enough to hit it is a library that wants a
 * cloud provider anyway; a write that exceeds it rejects rather than
 * silently losing data.
 */
const localStorageProvider: IStorageProvider = {
  name: StorageProviderKey.Local,

  load(fileName: string, defaultData: any) {
    try {
      const stored = localStorage.getItem(filePrefix + fileName);

      if (stored === null) {
        // Same contract as the cloud providers: a file that is not there yet
        // is created from the default rather than being an error.
        localStorage.setItem(filePrefix + fileName, JSON.stringify(defaultData));

        return Promise.resolve(defaultData);
      }

      return Promise.resolve(JSON.parse(stored));
    } catch (err) {
      return Promise.reject(err);
    }
  },

  saveAppData(fileName: string, fileData: any) {
    try {
      localStorage.setItem(filePrefix + fileName, JSON.stringify(fileData));

      return Promise.resolve(fileData);
    } catch (err) {
      // QuotaExceededError is the one that actually happens.
      return Promise.reject(err);
    }
  },
};

export default localStorageProvider;
